import { useEffect, useState } from "react";
import { resolveListingImages } from "@/lib/categoryImages";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  PlusCircle, RefreshCcw, CheckSquare, Undo2, Store, PoundSterling,
  Layers, Timer, Pencil, CheckCircle2, Recycle, Award, AlertTriangle, ListChecks, BadgePercent,
} from "lucide-react";
import { api, gbp, floorLabel } from "@/lib/api";
import RetailerLayout from "@/components/RetailerLayout";

const STATUS_TABS = ["all", "live", "reserved", "sold", "expired"];

export default function RetailerDashboard() {
  const [tab, setTab] = useState("all");
  const [listings, setListings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [l, a] = await Promise.all([
      api.get("/retailer/listings"),
      api.get("/retailer/analytics"),
    ]);
    setListings(l.data.listings || []);
    setAnalytics(a.data);
    setLoading(false);
  }
  useEffect(() => {
    load();
  }, []);

  async function markSold(id) {
    if (!window.confirm("Mark this remnant as sold? Any active reservation will be closed.")) return;
    await api.post(`/retailer/listings/${id}/mark-sold`);
    toast.success("Marked sold");
    load();
  }
  async function markAvailable(id) {
    await api.post(`/retailer/listings/${id}/mark-available`);
    toast.success("Marked available");
    load();
  }
  async function renew(id) {
    await api.post(`/retailer/listings/${id}/renew`);
    toast.success("Renewed");
    load();
  }
  async function confirmAvailable(id) {
    await api.post(`/retailer/listings/${id}/confirm-available`);
    toast.success("Confirmed as still available");
    load();
  }
  async function confirmAll() {
    const r = await api.post("/retailer/listings/confirm-available-all");
    toast.success(`Confirmed ${r.data.confirmed} listing${r.data.confirmed === 1 ? "" : "s"} as still available`);
    load();
  }

  const reminderDays = analytics?.stock_reminder_days ?? 7;
  function daysSinceConfirmed(l) {
    const ts = l.last_confirmed_available_at;
    if (!ts) return null;
    return Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  }
  function isOverdue(l) {
    if (l.status !== "live") return false;
    const ts = l.last_confirmed_available_at;
    if (!ts) return true;
    return Date.now() - new Date(ts).getTime() > reminderDays * 86400000;
  }
  const liveListings = listings.filter((l) => l.status === "live");
  const overdueCount = liveListings.filter(isOverdue).length;

  const filtered = tab === "all" ? listings : listings.filter((l) => l.status === tab);

  return (
    <RetailerLayout>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="retailer-dashboard">
        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Kpi icon={<Layers className="w-4 h-4" />} label="Live listings" value={analytics?.counts?.live ?? "—"} testId="kpi-live" />
          <Kpi icon={<Timer className="w-4 h-4" />} label="Reserved" value={analytics?.counts?.reserved ?? "—"} testId="kpi-reserved" />
          <Kpi icon={<Store className="w-4 h-4" />} label="Sold this month" value={analytics?.counts?.sold ?? "—"} testId="kpi-sold" />
          <Kpi icon={<PoundSterling className="w-4 h-4" />} label="Net revenue" value={gbp(analytics?.net_revenue_gbp || 0)} testId="kpi-revenue" />
        </div>

        {analytics?.impact && (
          <EcoBadge impact={analytics.impact} />
        )}

        {/* Stock-freshness reminder — in-app only (no emails sent) */}
        {liveListings.length > 0 && (
          <div
            className={`rounded-2xl border p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center gap-3 ${overdueCount > 0 ? "border-amber-300 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}
            data-testid="stock-freshness-banner"
          >
            <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ${overdueCount > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {overdueCount > 0 ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">
                {overdueCount > 0
                  ? `${overdueCount} listing${overdueCount === 1 ? "" : "s"} need${overdueCount === 1 ? "s" : ""} a quick availability check`
                  : "Your stock is up to date"}
              </div>
              <p className="text-sm text-slate-600 mt-0.5">
                {overdueCount > 0
                  ? `Confirm anything still in your shop and mark sold what's gone, so customers only see current stock. We suggest a check every ${reminderDays} days.`
                  : `Confirmed within the last ${reminderDays} days. Keep it current by confirming again or marking items sold when they go.`}
              </p>
            </div>
            <button
              onClick={confirmAll}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white text-sm font-semibold shrink-0 transition-colors"
              data-testid="confirm-all-available-btn"
            >
              <ListChecks className="w-4 h-4" /> Confirm all available
            </button>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-bold text-2xl text-slate-900">Your listings</h1>
            {analytics && analytics.commission_rate === 0 && (
              <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#0F3D2E] text-[#9fe0b6] px-3 py-1.5 rounded-full" data-testid="founding-retailer-badge">
                <BadgePercent className="w-3.5 h-3.5" /> Founding retailer · 0% commission
              </span>
            )}
          </div>
          <Link
            to="/retailer/new"
            className="fm-btn-primary inline-flex items-center gap-2 self-start h-12 text-base"
            data-testid="new-listing-btn"
          >
            <PlusCircle className="w-5 h-5" aria-hidden="true" /> New listing
          </Link>
        </div>

        <div className="flex flex-wrap gap-1 bg-white border border-slate-200 rounded-lg p-1 mb-4 w-fit">
          {STATUS_TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                tab === t ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
              data-testid={`tab-${t}`}
            >
              {t} {tab === t && analytics?.counts?.[t] != null && `(${analytics.counts[t]})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-slate-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-600">
            No listings in this tab.
          </div>
        ) : (
          <div className="grid gap-3">
            {filtered.map((l) => (
              <div
                key={l.id}
                className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
                data-testid={`row-${l.reference_code}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {resolveListingImages(l).photos[0] && (
                    <img
                      src={resolveListingImages(l).photos[0]}
                      alt=""
                      className="w-16 h-16 rounded-lg object-cover border border-slate-200"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="text-xs text-slate-500 font-mono">{l.reference_code}</div>
                    <div className="font-semibold text-slate-900 truncate capitalize">
                      {l.colour} {floorLabel(l.flooring_type)}
                    </div>
                    <div className="text-sm text-slate-600">
                      {l.matching_mode === "coverage" || !l.width_m || !l.length_m
                        ? `${l.packs_available ?? "—"} pack${l.packs_available === 1 ? "" : "s"}${l.total_coverage_m2 ? ` · ${l.total_coverage_m2}m²` : ""} · ${gbp(l.price_gbp)}`
                        : `${l.width_m}m × ${l.length_m}m · ${gbp(l.price_gbp)}`}
                    </div>
                    {l.status === "live" && (
                      isOverdue(l) ? (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700" data-testid={`freshness-overdue-${l.id}`}>
                          <AlertTriangle className="w-3 h-3" />
                          {l.last_confirmed_available_at ? `Confirm availability (last checked ${daysSinceConfirmed(l)}d ago)` : "Not yet confirmed available"}
                        </div>
                      ) : (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700" data-testid={`freshness-ok-${l.id}`}>
                          <CheckCircle2 className="w-3 h-3" />
                          {daysSinceConfirmed(l) === 0 ? "Confirmed today" : `Confirmed ${daysSinceConfirmed(l)}d ago`}
                        </div>
                      )
                    )}
                  </div>
                </div>
                <StatusPill status={l.status} />
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to={`/retailer/listings/${l.id}/edit`}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"
                    data-testid={`edit-${l.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Link>
                  {l.status === "live" && (
                    <>
                      <RowBtn onClick={() => confirmAvailable(l.id)} testId={`confirm-${l.id}`} icon={<CheckCircle2 className="w-3.5 h-3.5" />}>
                        Still available
                      </RowBtn>
                      <RowBtn onClick={() => markSold(l.id)} testId={`sold-${l.id}`} icon={<CheckSquare className="w-3.5 h-3.5" />}>
                        Mark sold
                      </RowBtn>
                    </>
                  )}
                  {(l.status === "sold" || l.status === "reserved") && (
                    <RowBtn onClick={() => markAvailable(l.id)} testId={`available-${l.id}`} icon={<Undo2 className="w-3.5 h-3.5" />}>
                      Mark available
                    </RowBtn>
                  )}
                  {l.status === "expired" && (
                    <RowBtn onClick={() => renew(l.id)} testId={`renew-${l.id}`} icon={<RefreshCcw className="w-3.5 h-3.5" />}>
                      Renew listing
                    </RowBtn>
                  )}
                  <Link
                    to={`/listing/${l.id}`}
                    className="text-xs text-slate-600 hover:text-[#E05915] transition-colors underline underline-offset-2"
                  >
                    View public
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </RetailerLayout>
  );
}

const BADGE_STYLES = {
  Gold: { ring: "ring-amber-300", chip: "bg-amber-100 text-amber-800", icon: "text-amber-500" },
  Silver: { ring: "ring-slate-300", chip: "bg-slate-100 text-slate-700", icon: "text-slate-400" },
  Bronze: { ring: "ring-orange-300", chip: "bg-orange-100 text-orange-800", icon: "text-orange-500" },
  Sprout: { ring: "ring-emerald-300", chip: "bg-emerald-100 text-emerald-800", icon: "text-emerald-500" },
};

function EcoBadge({ impact }) {
  const badge = impact.badge || {};
  const tier = badge.tier;
  const style = BADGE_STYLES[tier] || BADGE_STYLES.Sprout;
  const weight = badge.weight_kg ?? impact.diverted_weight_kg ?? 0;
  const next = badge.next_target_kg;
  const progress = next ? Math.min(100, Math.round((weight / next) * 100)) : 100;
  return (
    <div
      className={`bg-[#0F3D2E] text-white rounded-2xl p-5 sm:p-6 mb-6 relative overflow-hidden ${tier ? "ring-2 " + style.ring : ""}`}
      data-testid="retailer-eco-badge"
    >
      <div
        className="absolute inset-0 opacity-40"
        style={{ backgroundImage: "radial-gradient(circle at 90% 15%, rgba(42,90,59,0.7), transparent 45%)" }}
        aria-hidden="true"
      />
      <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex items-center gap-4 flex-1">
          <span className="grid place-items-center w-14 h-14 rounded-2xl bg-white/10 border border-white/20">
            {tier ? (
              <Award className={`w-7 h-7 ${style.icon}`} aria-hidden="true" />
            ) : (
              <Recycle className="w-7 h-7 text-[#9fe0b6]" aria-hidden="true" />
            )}
          </span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#9fe0b6]">
                Landfill diverted
              </span>
              {tier && (
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${style.chip}`}>
                  {tier} tier
                </span>
              )}
            </div>
            <div className="font-display font-bold text-3xl mt-1">
              {weight.toLocaleString("en-GB")}
              <span className="text-lg font-semibold text-[#9fe0b6]"> kg</span>
            </div>
            <div className="text-sm text-white/80 mt-0.5">
              {impact.sold_count} sold · ~{Math.round(impact.diverted_co2e_kg).toLocaleString("en-GB")}kg CO₂e avoided (est.)
            </div>
          </div>
        </div>
        {next && (
          <div className="sm:w-64">
            <div className="flex justify-between text-xs text-white/80 mb-1.5">
              <span>Next badge</span>
              <span className="font-semibold">{next.toLocaleString("en-GB")} kg</span>
            </div>
            <div className="h-2 rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#9fe0b6] transition-[width] duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="text-[11px] text-white/60 mt-1.5">
              Sell more surplus to level up your green badge.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={testId}>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
        {icon}
        {label}
      </div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    live: "bg-emerald-100 text-emerald-800",
    reserved: "bg-amber-100 text-amber-800",
    sold: "bg-slate-200 text-slate-700",
    expired: "bg-red-100 text-red-800",
    draft: "bg-slate-100 text-slate-500",
    hidden: "bg-slate-100 text-slate-500",
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100"}`}>
      {status}
    </span>
  );
}

function RowBtn({ children, onClick, testId, icon }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 transition-colors"
      data-testid={testId}
    >
      {icon}
      {children}
    </button>
  );
}
